import React, { useEffect, useMemo, useState } from 'react';
import { Globe, X, Search, Loader2, Pencil, ExternalLink, AlertCircle, RefreshCw } from 'lucide-react';
import { confirmar } from './Feedback';

interface CnesCadastroAdminProps {
  supabaseClient: any;
  currentUserId?: string;
  entidadesPorCnes: Record<string, string>;
  onClose: () => void;
}

interface LinhaCnes {
  cnes: string;
  entidade: string;
  usuarios: string[];
  site: string;
  atualizadoEm: string | null;
  atualizadoPor: string;
}

type Filtro = 'todos' | 'sem' | 'com';

const normalizarUrl = (valor: string): string | null => {
  let url = valor.trim();
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  try {
    const parsed = new URL(url);
    if (/^https?:$/.test(parsed.protocol) && parsed.hostname.includes('.')) return url;
  } catch {}
  return null;
};

export const CnesCadastroAdmin: React.FC<CnesCadastroAdminProps> = ({ supabaseClient, currentUserId, entidadesPorCnes, onClose }) => {
  const [linhas, setLinhas] = useState<LinhaCnes[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [editandoCnes, setEditandoCnes] = useState<string | null>(null);
  const [editValor, setEditValor] = useState('');
  const [editErro, setEditErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    setErro('');
    try {
      const [profilesRes, cadastroRes] = await Promise.all([
        supabaseClient.from('profiles').select('id, full_name, email, cnes'),
        supabaseClient.from('cnes_cadastro').select('cnes, site_transparencia, atualizado_em, atualizado_por')
      ]);
      if (profilesRes.error) throw profilesRes.error;
      if (cadastroRes.error) throw cadastroRes.error;

      const profiles: any[] = profilesRes.data || [];
      const nomePorId: Record<string, string> = {};
      const usuariosPorCnes: Record<string, string[]> = {};
      for (const p of profiles) {
        nomePorId[p.id] = p.full_name || p.email || '';
        const lista = String(p.cnes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
        for (const c of lista) {
          (usuariosPorCnes[c] ||= []).push(p.full_name || p.email || '—');
        }
      }

      const cadastroPorCnes: Record<string, any> = {};
      for (const r of (cadastroRes.data || [])) cadastroPorCnes[r.cnes] = r;

      const todosCnes = new Set<string>([...Object.keys(usuariosPorCnes), ...Object.keys(cadastroPorCnes)]);
      const resultado: LinhaCnes[] = Array.from(todosCnes).map(cnes => {
        const cad = cadastroPorCnes[cnes];
        return {
          cnes,
          entidade: entidadesPorCnes[cnes] || '',
          usuarios: usuariosPorCnes[cnes] || [],
          site: (cad?.site_transparencia || '').trim(),
          atualizadoEm: cad?.atualizado_em || null,
          atualizadoPor: cad?.atualizado_por ? (nomePorId[cad.atualizado_por] || '') : ''
        };
      });
      resultado.sort((a, b) => a.cnes.localeCompare(b.cnes));
      setLinhas(resultado);
    } catch (e: any) {
      setErro(e?.message || String(e));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const totalSemSite = useMemo(() => linhas.filter(l => !l.site).length, [linhas]);

  const linhasFiltradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return linhas.filter(l => {
      if (filtro === 'sem' && l.site) return false;
      if (filtro === 'com' && !l.site) return false;
      if (!q) return true;
      return l.cnes.includes(q)
        || l.entidade.toLowerCase().includes(q)
        || l.site.toLowerCase().includes(q)
        || l.usuarios.some(u => u.toLowerCase().includes(q));
    });
  }, [linhas, busca, filtro]);

  const iniciarEdicao = (linha: LinhaCnes) => {
    setEditandoCnes(linha.cnes);
    setEditValor(linha.site);
    setEditErro('');
  };

  const salvarEdicao = async () => {
    if (!editandoCnes) return;
    const vazio = !editValor.trim();
    const url = vazio ? null : normalizarUrl(editValor);
    if (!vazio && !url) {
      setEditErro('Endereço inválido. Ex.: https://www.instituicao.org.br/transparencia');
      return;
    }
    if (vazio && !(await confirmar(`Remover o site do CNES ${editandoCnes}?\n\nO usuário será solicitado a preencher novamente no próximo acesso.`, { rotuloConfirmar: 'Remover site' }))) return;

    setSalvando(true);
    setEditErro('');
    try {
      const agora = new Date().toISOString();
      const { error } = await supabaseClient.from('cnes_cadastro').upsert({
        cnes: editandoCnes,
        site_transparencia: url,
        atualizado_em: agora,
        atualizado_por: currentUserId || null
      }, { onConflict: 'cnes' });
      if (error) throw error;
      setLinhas(prev => prev.map(l => l.cnes === editandoCnes
        ? { ...l, site: url || '', atualizadoEm: agora, atualizadoPor: 'Você' }
        : l));
      setEditandoCnes(null);
    } catch (e: any) {
      setEditErro(`Erro ao salvar: ${e?.message || e}`);
    } finally {
      setSalvando(false);
    }
  };

  const filtroBtn = (valor: Filtro, label: string) => (
    <button
      type="button"
      onClick={() => setFiltro(valor)}
      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${filtro === valor ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-6xl shadow-2xl animate-slideUp flex flex-col max-h-[95vh] overflow-hidden">
        <div className="bg-gradient-to-r from-blue-800 to-blue-700 px-8 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-xl font-black text-white">Cadastro dos CNES</h2>
              <p className="text-xs text-blue-100 mt-0.5">Sites de transparência informados por estabelecimento</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-blue-600/40 transition-colors" aria-label="Fechar">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="px-8 py-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center gap-3 flex-shrink-0">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por CNES, entidade, usuário ou site"
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-300 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            {filtroBtn('todos', `Todos (${linhas.length})`)}
            {filtroBtn('sem', `Sem site (${totalSemSite})`)}
            {filtroBtn('com', `Com site (${linhas.length - totalSemSite})`)}
            <button type="button" onClick={carregar} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100" title="Recarregar">
              <RefreshCw className={`w-4 h-4 ${carregando ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {carregando ? (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Carregando...
            </div>
          ) : erro ? (
            <div className="m-8 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Erro ao carregar: {erro}
            </div>
          ) : linhasFiltradas.length === 0 ? (
            <p className="text-center py-16 text-sm text-gray-500">Nenhum CNES encontrado.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">CNES</th>
                  <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Entidade / Usuários</th>
                  <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Site de transparência</th>
                  <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Atualizado</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {linhasFiltradas.map(l => (
                  <tr key={l.cnes} className="hover:bg-gray-50/60 align-top">
                    <td className="px-5 py-3 font-mono font-bold text-gray-900">{l.cnes}</td>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-gray-800">{l.entidade || '—'}</p>
                      {l.usuarios.length > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">{l.usuarios.join(', ')}</p>
                      )}
                    </td>
                    <td className="px-5 py-3 min-w-[260px]">
                      {editandoCnes === l.cnes ? (
                        <div className="space-y-1.5">
                          <input
                            type="url"
                            autoFocus
                            value={editValor}
                            onChange={(e) => setEditValor(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') salvarEdicao(); if (e.key === 'Escape') setEditandoCnes(null); }}
                            placeholder="https://..."
                            className="w-full px-3 py-2 rounded-lg border border-blue-300 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                          {editErro && <p className="text-xs text-red-600 font-semibold">{editErro}</p>}
                          <div className="flex gap-2">
                            <button type="button" onClick={salvarEdicao} disabled={salvando}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                              {salvando ? 'Salvando...' : 'Salvar'}
                            </button>
                            <button type="button" onClick={() => setEditandoCnes(null)}
                              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs font-bold">Cancelar</button>
                          </div>
                        </div>
                      ) : l.site ? (
                        <a href={l.site} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline break-all inline-flex items-start gap-1">
                          {l.site} <ExternalLink className="w-3 h-3 mt-1 flex-shrink-0" />
                        </a>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">Não informado</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {l.atualizadoEm ? (
                        <>
                          <p>{new Date(l.atualizadoEm).toLocaleString('pt-BR')}</p>
                          {l.atualizadoPor && <p className="text-gray-400">{l.atualizadoPor}</p>}
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {editandoCnes !== l.cnes && (
                        <button type="button" onClick={() => iniciarEdicao(l)} className="p-2 rounded-lg text-gray-500 hover:text-blue-700 hover:bg-blue-50" title="Editar site">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
